/**
 * frontend/src/pages/valuation/_TuningControls.tsx
 *
 * VALUATION-B3 guardrail 3 — "every weight change carries a rationale and
 * stays within professional bounds: a justified number, not a free number."
 *
 * The seven knobs `derive_candidate()` accepts, and nothing else. What is
 * NOT here is the point: no control for the value-driver rubric (that would
 * make the driver score typeable by the back door — guardrail 1), and none
 * for the benchmark table (that would move the fence guardrail 2 depends on).
 *
 * The slider min/max below mirror `DEFAULT_TUNING_BOUNDS` in
 * core/valuation/config_schema.py. They are a CONVENIENCE, not the
 * enforcement: `validate_config()` re-checks every value and the endpoint
 * 400s on a violation regardless of what this file allows. If the two ever
 * disagree, the server is right — these numbers are a copy and should be
 * corrected here, never relaxed there.
 *
 * The one place this component does arithmetic is the income/market pair:
 * the weights must sum to 1.0, so moving one moves the other. That is
 * honouring a stated constraint rather than deriving a value — without it the
 * only thing the valuator could do is submit invalid pairs and read 400s.
 */
import { Textarea } from '@/components/ui/textarea'
import { fracPct, money, pct } from '@/utils/format'
import type { TuningKnobs } from '@/types/valuationB3'

export interface KnobSpec {
  field: keyof TuningKnobs
  label: string
  help: string
  min: number
  max: number
  step: number
  format: (v: number) => string
}

/** Bounds copied from DEFAULT_TUNING_BOUNDS — see module docstring. */
export const KNOBS: KnobSpec[] = [
  {
    field: 'income_weight',
    label: 'Income approach weight',
    help: 'Market weight moves with it — the two must sum to 100%. DCF stays a reference cross-check, out of the blend.',
    min: 0.2,
    max: 0.8,
    step: 0.05,
    format: v => fracPct(v),
  },
  {
    field: 'max_spread_pct',
    label: 'Convergence spread ceiling',
    help: 'How far the income and market indications may sit apart before the run is flagged divergent.',
    min: 5,
    max: 50,
    step: 1,
    format: v => pct(v, 0),
  },
  {
    field: 'low_band_pct',
    label: 'Band — below the point',
    help: 'How far under the concluded point the reported low sits.',
    min: 2.5,
    max: 30,
    step: 0.5,
    format: v => pct(v, 1),
  },
  {
    field: 'high_band_pct',
    label: 'Band — above the point',
    help: 'How far over the concluded point the reported high sits.',
    min: 2.5,
    max: 30,
    step: 0.5,
    format: v => pct(v, 1),
  },
  {
    field: 'crossover_earnings_threshold',
    label: 'SDE → EBITDA crossover',
    help: 'Normalized earnings above this are valued on EBITDA rather than SDE.',
    min: 250_000,
    max: 10_000_000,
    step: 50_000,
    format: v => money(v),
  },
  {
    field: 'customer_concentration_trigger_pct',
    label: 'Customer concentration trigger',
    help: 'The single-customer share of revenue at which concentration is disclosed as a risk.',
    min: 5,
    max: 50,
    step: 1,
    format: v => pct(v, 0),
  },
]

function Knob({
  spec,
  value,
  baseline,
  onChange,
  disabled,
}: {
  spec: KnobSpec
  value: number
  baseline: number
  onChange: (field: keyof TuningKnobs, value: number) => void
  disabled?: boolean
}) {
  const moved = Math.abs(value - baseline) > 1e-9
  const id = `knob-${spec.field}`
  return (
    <div className="rounded-lg border border-pal-line p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <label htmlFor={id} className="text-sm font-medium text-pal-ink">
          {spec.label}
        </label>
        <span className={`text-sm font-mono ${moved ? 'text-[#0d1f3c] font-semibold' : 'text-gray-600'}`}>
          {spec.format(value)}
        </span>
      </div>
      <input
        id={id}
        type="range"
        className="mt-2 w-full accent-[#0d1f3c]"
        min={spec.min}
        max={spec.max}
        step={spec.step}
        value={value}
        disabled={disabled}
        onChange={e => onChange(spec.field, Number(e.target.value))}
      />
      <div className="flex justify-between text-[11px] text-pal-ink-faint">
        <span>{spec.format(spec.min)}</span>
        <span>{spec.format(spec.max)}</span>
      </div>
      <p className="mt-1 text-xs text-pal-ink-muted">{spec.help}</p>
      {moved && (
        <p className="mt-1 text-xs text-pal-navy">
          Changed from the active config's {spec.format(baseline)}.
        </p>
      )}
    </div>
  )
}

export function TuningControls({
  values,
  baseline,
  marketWeight,
  rationale,
  tuned,
  disabled,
  onChange,
  onRationale,
  onReset,
}: {
  values: Record<string, number>
  baseline: Record<string, number>
  /** Derived from income_weight — shown so the pairing is not a hidden effect. */
  marketWeight: number
  rationale: string
  tuned: boolean
  disabled?: boolean
  onChange: (field: keyof TuningKnobs, value: number) => void
  onRationale: (value: string) => void
  onReset: () => void
}) {
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        {KNOBS.map(spec => (
          <Knob
            key={spec.field}
            spec={spec}
            value={values[spec.field]}
            baseline={baseline[spec.field]}
            onChange={onChange}
            disabled={disabled}
          />
        ))}
      </div>

      <p className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
        Blend: <span className="font-mono">{fracPct(values.income_weight)}</span> income ·{' '}
        <span className="font-mono">{fracPct(marketWeight)}</span> market ·{' '}
        <span className="font-mono">0%</span> DCF (reference cross-check only).
      </p>

      <div>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <label htmlFor="tuning-rationale" className="text-sm font-medium">
            Rationale for these weights
            {tuned && <span className="ml-1 text-red-600">*</span>}
          </label>
          {tuned && (
            <button
              type="button"
              className="text-xs text-pal-ink-muted underline hover:text-gray-800"
              onClick={onReset}
              disabled={disabled}
            >
              Reset to the active config
            </button>
          )}
        </div>
        <Textarea
          id="tuning-rationale"
          rows={3}
          value={rationale}
          disabled={disabled}
          onChange={e => onRationale(e.target.value)}
          placeholder="Why these weights, for this business — e.g. earnings history is thin, so the market approach carries more."
        />
        {tuned && !rationale.trim() && (
          <p className="mt-1 text-xs text-amber-700">
            A tuned methodology cannot be signed without a rationale. The preview
            below still recomputes; sign-off stays blocked until this is filled in.
          </p>
        )}
      </div>
    </div>
  )
}
